import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bell, 
  Plus, 
  Trash2, 
  TrendingUp, 
  TrendingDown, 
  Search,
  Target,
  CheckCircle,
  Clock,
  Loader2,
  ExternalLink
} from "lucide-react";

export const PriceAlertsPage = () => {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [newAlert, setNewAlert] = useState({
    target_nav: "",
    alert_type: "above"
  });

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const searchFunds = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/funds/search`, { params: { q: searchQuery } });
      setSearchResults(response.data.funds || []);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) searchFunds();
      else setSearchResults([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchFunds]);

  const selectFund = (fund) => {
    setSelectedFund({
      scheme_code: fund.schemeCode || fund.scheme_code,
      scheme_name: fund.schemeName || fund.scheme_name
    });
    setSearchQuery("");
    setSearchResults([]);
  };

  const createAlert = async () => {
    if (!selectedFund || !newAlert.target_nav) {
      toast.error("Please select a fund and enter target NAV");
      return;
    }
    try {
      await axios.post(`${API}/alerts`, {
        ...selectedFund,
        target_nav: parseFloat(newAlert.target_nav),
        alert_type: newAlert.alert_type
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Price alert created!");
      setIsDialogOpen(false);
      setSelectedFund(null);
      setNewAlert({ target_nav: "", alert_type: "above" });
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to create alert");
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Alert deleted");
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
    } catch (error) {
      toast.error("Failed to delete alert");
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "above": return <TrendingUp className="w-4 h-4 text-green-500" />;
      case "below": return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Target className="w-4 h-4 text-primary" />;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="price-alerts-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold mb-1">
              Price <span className="gold-text">Alerts</span>
            </h1>
            <p className="text-muted-foreground">Get notified when fund NAV reaches your target</p>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-alert-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Create Price Alert</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Fund Search */}
                <div className="space-y-2">
                  <Label>Select Fund</Label>
                  {selectedFund ? (
                    <div className="p-3 bg-secondary/50 rounded-lg flex items-center justify-between">
                      <span className="text-sm">{selectedFund.scheme_name}</span>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedFund(null)}>
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search funds..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-secondary/50 border-white/10"
                        data-testid="alert-fund-search"
                      />
                      {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />}
                    </div>
                  )}
                  {searchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {searchResults.slice(0, 5).map((fund) => (
                        <div
                          key={fund.schemeCode || fund.scheme_code}
                          className="p-2 bg-secondary/30 rounded cursor-pointer hover:bg-secondary/50 text-sm"
                          onClick={() => selectFund(fund)}
                        >
                          {fund.schemeName || fund.scheme_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Alert Type */}
                <div className="space-y-2">
                  <Label>Alert When NAV is</Label>
                  <Select value={newAlert.alert_type} onValueChange={(v) => setNewAlert({ ...newAlert, alert_type: v })}>
                    <SelectTrigger className="bg-secondary/50 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="above">Above Target</SelectItem>
                      <SelectItem value="below">Below Target</SelectItem>
                      <SelectItem value="exact">Reaches Target (±₹0.50)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Target NAV */}
                <div className="space-y-2">
                  <Label>Target NAV (₹)</Label>
                  <Input
                    type="number"
                    placeholder="100.00"
                    value={newAlert.target_nav}
                    onChange={(e) => setNewAlert({ ...newAlert, target_nav: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                    data-testid="target-nav-input"
                  />
                </div>

                <Button onClick={createAlert} className="w-full btn-primary" data-testid="submit-alert-btn">
                  Create Alert
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Alerts List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Active Alerts ({alerts.filter(a => a.is_active && !a.triggered).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              </div>
            ) : alerts.length > 0 ? (
              <div className="space-y-4">
                {alerts.map((alert, index) => (
                  <div
                    key={alert.alert_id || index}
                    className={`p-4 rounded-xl ${alert.triggered ? 'bg-green-500/10 border border-green-500/20' : 'bg-secondary/30'}`}
                    data-testid={`alert-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Link to={`/research/${alert.scheme_code}`} className="font-medium hover:text-primary flex items-center gap-1">
                            {alert.scheme_name}
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                          {alert.triggered ? (
                            <Badge className="bg-green-500/20 text-green-500">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Triggered
                            </Badge>
                          ) : (
                            <Badge className="bg-primary/20 text-primary">
                              <Clock className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            {getAlertIcon(alert.alert_type)}
                            Alert when NAV is {alert.alert_type} {formatCurrency(alert.target_nav)}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => deleteAlert(alert.alert_id)}
                        data-testid={`delete-alert-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No Price Alerts</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Create alerts to get notified when fund NAV reaches your target
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="glass-card">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              How Price Alerts Work
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Set a target NAV for any mutual fund</li>
              <li>• Choose to be notified when NAV goes above, below, or reaches your target</li>
              <li>• Receive email notifications when your alert is triggered</li>
              <li>• Alerts are checked periodically throughout the trading day</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
